import { caretOffsetAfterFormat, formatAmountTyping, hasDecimalMarker } from '@/utils/amountFormatter';

/**
 * Custom directive for currency formatting in Vue 2.
 * Supports Element UI el-input and native input.
 * Usage: v-currency-mask="callback"
 * callback(formattedValue, numericValue)
 */
export default {
  bind(el, binding) {
    const input = el.tagName === 'INPUT' ? el : el.querySelector('input');
    if (!input) return;

    input.currencyHandler = (event) => {
      if (input.isUpdating) return;

      const { value, selectionStart } = input;

      // Count digits before cursor to restore position
      const textBeforeCursor = value.substring(0, selectionStart);
      const digitsBeforeCursor = textBeforeCursor.replace(/\D/g, '').length;
      const hadDecimalBeforeCursor = hasDecimalMarker(textBeforeCursor);

      const { formattedValue, numericValue } = formatAmountTyping(value);

      if (formattedValue === '') {
        if (typeof binding.value === 'function') {
          binding.value('', null);
        }
        return;
      }

      // Update input value directly to avoid Vue's reactive delay
      if (input.value !== formattedValue) {
        input.value = formattedValue;
        const finalOffset = caretOffsetAfterFormat(formattedValue, digitsBeforeCursor, hadDecimalBeforeCursor);
        input.setSelectionRange(finalOffset, finalOffset);
      }

      // Trigger callback with numeric value
      if (typeof binding.value === 'function') {
        binding.value(formattedValue, numericValue);
      }

      // input.dispatchEvent(new Event('input'));
      // To avoid infinite loop and correctly update el-input / v-model:
      // We stop the event from bubbling and trigger a new one if necessary,
      // but usually for el-input it's better to just let the event continue if we didn't change the value manually too much.
      // Actually, if we changed input.value, we SHOULD trigger input event so Vue sees it.
      // To prevent infinite loop, we can check a flag.
      if (!input.isUpdating) {
        input.isUpdating = true;
        input.dispatchEvent(new Event('input', { bubbles: true }));
        input.isUpdating = false;
      }
    };

    input.addEventListener('input', input.currencyHandler);
  },
  unbind(el) {
    const input = el.tagName === 'INPUT' ? el : el.querySelector('input');
    if (input && input.currencyHandler) {
      input.removeEventListener('input', input.currencyHandler);
      delete input.currencyHandler;
      delete input.isUpdating;
    }
  },
};
